import React, { useCallback, useRef, useState } from "react";
import { OwnershipRemovalDialog } from "../sections/common/OwnershipChangeDialogs";
import {
  applyRemovedPartyDocuments,
  applySignatoryChoices,
  linkedSignatories,
  loadRemovedPartyDocuments,
} from "../../utils/ownershipPartyRemoval";

/**
 * Removing ownership parties (Edit diagram, Reset, client type change) and what is attached to
 * them. requestRemoval(parties, { mode, onConfirmed }) — mode "edit": approved documents kept or
 * deleted (asked); mode "delete": everything deleted. Linked signatories: asked, one by one.
 * The warning only appears when the parties have approved documents (edit) or linked signatories.
 * onConfirmed({ signatories }) receives the signatory list to save; onCancelled() on "Back".
 */
export default function useOwnershipRemoval(form) {
  const [dialog, setDialog] = useState(null);
  const [busy, setBusy] = useState(false);
  const pendingRef = useRef(null);

  const finish = useCallback(async (pending, { keepDocuments, signatoryChoices }) => {
    try {
      await applyRemovedPartyDocuments(pending.documents, pending.parties, {
        keepApproved: pending.mode === "edit" && keepDocuments,
      });
    } catch (error) {
      console.warn("Documents of removed parties could not be updated:", error?.message || error);
    }
    pending.onConfirmed?.({ signatories: applySignatoryChoices(form?.ownership?.signatories, signatoryChoices || {}) });
  }, [form]);

  const requestRemoval = useCallback(async (parties, { mode = "delete", onConfirmed, onCancelled, title, intro, applyLabel, backLabel, signatoriesTitle, kindLabels } = {}) => {
    const list = Array.isArray(parties) ? parties : [];
    if (!list.length) {
      onConfirmed?.({ signatories: form?.ownership?.signatories || [] });
      return;
    }
    const signatories = linkedSignatories(form?.ownership, list);
    const documents = await loadRemovedPartyDocuments(form, list);
    const signatoryRows = new Set(signatories.map((s) => `${s._personLink?.collection}:${s._personLink?.rowId}`));
    const described = list.map((party) => ({
      ...party,
      approvedCount: documents[party.key]?.approved?.length || 0,
      signatory: party.rows.some((r) => signatoryRows.has(`${r.collection}:${r.row?._rowId}`) ||
        signatoryRows.has(`${r.collection === "ubos" ? "controllingPersonsNatural" : "directShareholders"}:${r.row?._rowId}`)),
    }));
    const hasApproved = described.some((p) => p.approvedCount);
    const pending = { parties: described, documents, mode, onConfirmed, onCancelled };
    const needsDialog = signatories.length || (mode === "edit" && hasApproved);
    if (!needsDialog) {
      await finish(pending, { keepDocuments: true, signatoryChoices: {} });
      return;
    }
    pendingRef.current = pending;
    setDialog({ parties: described, signatories, showDocumentChoice: mode === "edit" && hasApproved, title, intro, applyLabel, backLabel, signatoriesTitle, kindLabels });
  }, [form, finish]);

  const element = (
    <OwnershipRemovalDialog
      open={!!dialog}
      title={dialog?.title}
      intro={dialog?.intro}
      applyLabel={dialog?.applyLabel}
      backLabel={dialog?.backLabel}
      signatoriesTitle={dialog?.signatoriesTitle}
      kindLabels={dialog?.kindLabels}
      parties={dialog?.parties || []}
      signatories={dialog?.signatories || []}
      showDocumentChoice={dialog?.showDocumentChoice}
      busy={busy}
      onBack={() => {
        const pending = pendingRef.current;
        pendingRef.current = null;
        setDialog(null);
        pending?.onCancelled?.();
      }}
      onApply={async (choice) => {
        const pending = pendingRef.current;
        if (!pending) return;
        setBusy(true);
        try {
          await finish(pending, choice);
        } finally {
          setBusy(false);
          pendingRef.current = null;
          setDialog(null);
        }
      }}
    />
  );

  return { requestRemoval, removalDialog: element };
}
